import { useShopI18n } from '../i18n/ShopI18n'
import { useEffect, useState } from 'react'
import { useAssetUrl } from '../hooks/useAsset'
import { logger } from '../utils/logger'

interface IframeResponseMessage {
    type: 'cancel' | 'success' | 'pending' | 'error' | 'payment.open_support_chat' | 'open_terms_and_conditions'
    refId?: string
    transactionId?: string
}

interface CheckoutModalProps {
    isOpen: boolean
    checkoutUrl: string
    onClose: () => void
    onSuccess?: (refId?: string, transactionId?: string) => void
    onError?: (refId?: string, transactionId?: string) => void
    onPending?: (refId?: string, transactionId?: string) => void
    onCancel?: () => void
}

function CheckoutModal({ isOpen, checkoutUrl, onClose, onSuccess, onError, onPending, onCancel }: CheckoutModalProps) {
    const { t } = useShopI18n()
    const [isClosing, setIsClosing] = useState(false)
    const [shouldRender, setShouldRender] = useState(isOpen)
    const [animationComplete, setAnimationComplete] = useState(false)
    
    // Get shop background image
    const shopBgUrl = useAssetUrl('pages_popups/layers/shop/bg.png')

    useEffect(() => {
        logger.log('COMPONENT', '📌 isOpen changed:', isOpen)
        if (isOpen) {
            logger.log('COMPONENT', '🚀 Opening modal')
            setShouldRender(true)
            setIsClosing(false)
            setAnimationComplete(false)
            document.body.style.overflow = 'hidden'
            // Wait for animation to complete before showing iframe
            const timer = setTimeout(() => {
                setAnimationComplete(true)
            }, 500) // Animation duration (400ms) + delay (100ms) + buffer
            return () => clearTimeout(timer)
        } else if (shouldRender) {
            logger.log('COMPONENT', '🚪 Starting close animation')
            setAnimationComplete(false)
            // Start closing animation
            setIsClosing(true)
            // Wait for animation to complete before unmounting
            const timer = setTimeout(() => {
                logger.log('COMPONENT', '👋 Unmounting modal')
                setShouldRender(false)
                document.body.style.overflow = 'unset'
            }, 400) // Match animation duration
            return () => clearTimeout(timer)
        }
    }, [isOpen, shouldRender])

    useEffect(() => {
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [])

    // Listen for messages from the iframe
    useEffect(() => {
        if (!isOpen) return

        const closeModal = () => {
            logger.log('COMPONENT', '🚪 Closing modal from iframe event')
            setIsClosing(true)
            setTimeout(() => {
                onClose()
            }, 400)
        }

        const handleMessage = (event: MessageEvent) => {
            if (!event) {
                return
            }

            // Validate origin for security
            const validOrigins = [
                'payment.whow.net',      // live
                'payment-staging.whow.net', // staging
                'payment.v2',            // dev
                'papi.suite.v2'          // dev
            ]

            const isValidOrigin = validOrigins.some(origin => event.origin.includes(origin))

            if (!isValidOrigin) {
                logger.log('COMPONENT', '⚠️ Message from invalid origin:', event.origin)
                return
            }

            const data: IframeResponseMessage = event.data

            if (!data || !data.type) {
                logger.error('COMPONENT', '❌ Invalid iframe data', data)
                return
            }

            logger.log('COMPONENT', '📨 Received message from iframe:', data)

            switch (data.type) {
                case 'cancel':
                    onCancel?.()
                    logger.log('COMPONENT', '🚫 User cancelled checkout')
                    closeModal()
                    break

                case 'success':
                    logger.log('COMPONENT', '✅ Payment successful!', { refId: data.refId, transactionId: data.transactionId })
                    onSuccess?.(data.refId, data.transactionId)
                    closeModal()
                    break

                case 'pending':
                    logger.log('COMPONENT', '⏳ Payment pending', { refId: data.refId, transactionId: data.transactionId })
                    onPending?.(data.refId, data.transactionId)
                    closeModal()
                    break

                case 'error':
                    logger.log('COMPONENT', '❌ Payment error', { refId: data.refId, transactionId: data.transactionId })
                    onError?.(data.refId, data.transactionId)
                    closeModal()
                    break

                case 'payment.open_support_chat':
                    logger.log('COMPONENT', '💬 Open support chat requested')
                    // TODO: Implement support chat
                    break

                case 'open_terms_and_conditions':
                    logger.log('COMPONENT', '📄 Open terms and conditions requested')
                    // TODO: Implement terms and conditions
                    break

                default:
                    logger.error('COMPONENT', '❌ Invalid iframe data type', data?.type, data)
                    break
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    }, [isOpen, onClose, onSuccess, onError, onPending, onCancel])

    const handleClose = () => {
        logger.log('COMPONENT', '✅ Close button clicked!')
        logger.log('COMPONENT', 'Current state:', { isOpen, isClosing, shouldRender })
        setIsClosing(true)
        setTimeout(() => {
            logger.log('COMPONENT', 'Calling onClose callback')
            onClose()
        }, 400) // Match animation duration
    }

    if (!shouldRender) return null

    logger.log('COMPONENT', '🎨 Rendering modal with state:', { isOpen, isClosing, shouldRender, hasCheckoutUrl: !!checkoutUrl })

    return (
        <div className="fixed inset-0 z-100 flex items-center justify-center">
            {/* Backdrop */}
            <div 
                className={`absolute inset-0 bg-black/80 backdrop-blur-xs ${
                    isClosing ? 'animate-fade-out-overlay' : 'animate-fade-in-overlay'
                }`}
                onClick={() => {
                    logger.log('COMPONENT', '🖱️ Backdrop clicked!')
                    handleClose()
                }}
            ></div>

            {/* Modal Container with Button */}
            <div 
                className={`relative m-4 flex flex-col ${
                    isClosing ? 'animate-scale-out' : 'animate-scale-in'
                }`}
                style={{
                    width: 'min(95vw, 1400px)',
                    maxWidth: '95vw',
                    height: 'min(95vh, 1000px)',
                    maxHeight: '95vh',
                    minHeight: 'min(700px, 90vh)',
                    minWidth: 0
                }}
            >
                {/* Close Button - Positioned OUTSIDE modal content as absolute to parent container */}
                <button
                    onClick={(e) => {
                        logger.log('COMPONENT', '🖱️ Button onClick event fired!')
                        logger.log('COMPONENT', 'Event details:', e)
                        e.stopPropagation()
                        e.preventDefault()
                        handleClose()
                    }}
                    onMouseDown={() => {
                        logger.log('COMPONENT', '🖱️ Button mouseDown event fired!')
                    }}
                    onMouseEnter={() => {
                        logger.log('COMPONENT', '🖱️ Mouse entered close button')
                    }}
                    className="absolute -top-2 -right-2 z-200 w-12 h-12 flex items-center justify-center rounded-full bg-black/70 hover:bg-black/90 backdrop-blur-xs transition-all hover:scale-110 text-white shadow-2xl border-2 border-white/30 pointer-events-auto"
                    style={{ pointerEvents: 'auto' }}
                    aria-label={t("Close")}
                >
                    <svg
                        className="w-6 h-6 pointer-events-none"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={3}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                {/* Modal Content */}
                <div 
                    className="relative w-full h-full rounded-2xl shadow-2xl z-0"
                    style={{
                        ...(shopBgUrl ? {
                            backgroundImage: `url(${shopBgUrl})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                        } : {}),
                        overflow: 'hidden',
                        display: 'block'
                    }}
                >
                    {/* Loading State */}
                    {!checkoutUrl && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1
                        }}>
                            <div className="text-center bg-black/50 backdrop-blur-xs p-8 rounded-2xl">
                                <div className="w-16 h-16 border-4 border-[#862ce5] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                <p className="text-white font-medium">{t("Loading checkout...")}</p>
                            </div>
                        </div>
                    )}

                    {/* Iframe */}
                    {checkoutUrl && animationComplete && (
                        <iframe
                            src={checkoutUrl}
                            className="border-0"
                            title={t("Checkout")}
                            sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                            style={{
                                display: 'block',
                                margin: 0,
                                padding: 0,
                                width: '90%',
                                height: '90%',
                                border: 'none',
                                borderRadius: 0,
                                clipPath: 'none',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                boxSizing: 'border-box'
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

export default CheckoutModal
